import { useMsal } from "@azure/msal-react";
import {
    Box,
    Button,
    Flex,
    Group,
    Modal,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFilterFnsState,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { useMemo, useRef, useState } from "react";
import { deleteAPI, getAPI, postAPI } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

type Term = {
    id: string;
    term: string;
    full_name: string;
    description: string;
    creation_date?: string;
    creator?: string;
};

type TermsApiResponse = {
    data: Term[];
};

interface MutationParams {
    data: Partial<Term>;
}

const useTermOperations = () => {
    const { instance } = useMsal();

    const createTerm = async ({ data }: MutationParams) => {
        const response = await postAPI({
            url: `${BACKEND_URL}/v1/system/terms`,
            instance,
            payload: data,
        });

        if (!response.ok) {
            throw new Error("Failed to create term");
        }

        return response.json();
    };

    const deleteTerm = async ({ data }: MutationParams) => {
        const response = await deleteAPI({
            url: `${BACKEND_URL}/v1/system/terms?id=${data.id}`,
            instance,
        });

        if (!response.ok) {
            throw new Error("Failed to delete term");
        }

        return response.json();
    };

    return { createTerm, deleteTerm };
};

const ManageTerms = () => {
    const columns = useMemo<MRT_ColumnDef<Term>[]>(
        () => [
            {
                accessorKey: "term",
                header: "Term",
                accessorFn: (row) => row?.term ?? "",
            },
            {
                accessorKey: "full_name",
                header: "Full Name",
                accessorFn: (row) => row?.full_name ?? "",
            },
            {
                accessorKey: "description",
                header: "Description",
                accessorFn: (row) => row?.description ?? "",
            },
            {
                accessorKey: "creation_date",
                header: "Creation Date",
                accessorFn: (row) =>
                    row?.creation_date ? new Date(row.creation_date).toLocaleString() : "",
            },
            {
                accessorKey: "creator",
                header: "Creator",
                accessorFn: (row) => row?.creator ?? "",
            },
        ],
        [],
    );

    const isAdmin = useAdmin();
    const { instance } = useMsal();

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [columnFilterFns, _setColumnFilterFns] = useState<MRT_ColumnFilterFnsState>(
        Object.fromEntries(columns.map(({ accessorKey }) => [accessorKey, "contains"])),
    );
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "term", desc: false }]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const fetchURL = useMemo(() => {
        const url = new URL("/v1/system/terms", BACKEND_URL);
        url.searchParams.set("start", `${pagination.pageIndex * pagination.pageSize}`);
        url.searchParams.set("size", `${pagination.pageSize}`);
        url.searchParams.set("filters", JSON.stringify(columnFilters ?? []));
        url.searchParams.set("filterModes", JSON.stringify(columnFilterFns ?? {}));
        url.searchParams.set("globalFilter", globalFilter ?? "");
        url.searchParams.set("sorting", JSON.stringify(sorting ?? []));
        return url;
    }, [columnFilterFns, columnFilters, globalFilter, pagination, sorting]);

    const { data, isLoading, refetch } = useInfiniteQuery<TermsApiResponse>({
        queryKey: ["terms", fetchURL.href],
        queryFn: async () => {
            const response = await getAPI({ url: fetchURL.href, instance });
            const data = await response.json();
            return data;
        },
        initialPageParam: 0,
        getNextPageParam: () => undefined,
        placeholderData: (previousData) => previousData,
        enabled: isAdmin,
    });

    const { createTerm, deleteTerm } = useTermOperations();

    const fetchedTerms = data?.pages.flatMap((page) => (Array.isArray(page) ? page : [])) ?? [];

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [isRefetching, setIsRefetching] = useState(false);

    const fetchMoreOnBottomReached = async (containerRefElement?: HTMLDivElement) => {
        if (containerRefElement) {
            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            if (
                scrollHeight - scrollTop - clientHeight < 400 &&
                !isRefetching &&
                data?.pages[data.pages.length - 1]?.data?.length === pagination.pageSize
            ) {
                setIsRefetching(true);
                setPagination((old) => ({ ...old, pageIndex: old.pageIndex + 1 }));
                setIsRefetching(false);
            }
        }
    };

    const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
    const [detailsModalOpened, setDetailsModalOpened] = useState(false);
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleOpenDetailsModal = (term: Term) => {
        setSelectedTerm(term);
        setDetailsModalOpened(true);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            if (!selectedTerm) throw new Error("No term selected");

            await deleteTerm({ data: { id: selectedTerm.id } });

            notifications.show({
                title: "Success",
                message: "Term deleted successfully",
                color: "green",
            });
            setDeleteModalOpened(false);
            setDetailsModalOpened(false);

            await new Promise((resolve) => setTimeout(resolve, 1000));
            await refetch();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to delete term",
                color: "red",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const [createModalOpened, setCreateModalOpened] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const createForm = useForm({
        initialValues: {
            term: "",
            full_name: "",
            description: "",
        },
        validate: {
            term: (value) => (!value?.trim() ? "Term abbreviation is required" : null),
            full_name: (value, values) =>
                !value?.trim() && !values.description?.trim()
                    ? "Full name or description is required"
                    : null,
            description: (value, values) =>
                !value?.trim() && !values.full_name?.trim()
                    ? "Full name or description is required"
                    : null,
        },
    });

    const handleCreate = async (values: typeof createForm.values) => {
        setIsCreating(true);
        try {
            const result = await createTerm({ data: values });

            if (!result) throw new Error("Failed to create term");

            notifications.show({
                title: "Success",
                message: "Term created successfully",
                color: "green",
            });
            setCreateModalOpened(false);
            createForm.reset();

            await new Promise((resolve) => setTimeout(resolve, 1000));
            await refetch();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to create term",
                color: "red",
            });
        } finally {
            setIsCreating(false);
        }
    };

    const table = useMantineReactTable({
        // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        columns: columns as any,
        layoutMode: "grid",
        data: fetchedTerms,
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        renderTopToolbarCustomActions: () => (
            <Flex align="center" gap="md">
                <Text size="sm" c="dimmed">
                    {`Total: ${fetchedTerms.length}, (Showing ${table.getFilteredRowModel().rows.length})`}
                </Text>
            </Flex>
        ),
        enableColumnResizing: true,
        enableFilters: true,
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row) => row.id,
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableDensityToggle: false,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { maxHeight: "calc(100vh - 280px)" },
            onScroll: (event) => fetchMoreOnBottomReached(event.currentTarget),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleOpenDetailsModal(row.original),
            style: {
                cursor: isLoading ? "wait" : "pointer",
                opacity: isLoading ? 0.7 : 1,
            },
        }),
        state: {
            isLoading,
            showProgressBars: isRefetching,
            columnFilters,
            globalFilter,
            sorting,
        },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
    });

    return (
        <>
            <Flex px="xl" pb="xl" direction="column" gap="md">
                <Flex gap="md" align="center" justify="space-between">
                    <Title lh={2} c="purple.8" order={2} tt="uppercase">
                        Manage Terms
                    </Title>
                    <Button
                        onClick={() => {
                            createForm.reset();
                            setCreateModalOpened(true);
                        }}
                        variant="filled"
                    >
                        Create New Term
                    </Button>
                </Flex>
            </Flex>

            <MantineReactTable table={table} />

            <Modal
                closeButtonProps={{ "aria-label": "Close modal" }}
                opened={createModalOpened}
                onClose={() => {
                    setCreateModalOpened(false);
                    createForm.reset();
                }}
                title="Create New Term"
                size="md"
            >
                <Box component="form" onSubmit={createForm.onSubmit(handleCreate)}>
                    <Stack gap="md">
                        <TextInput
                            required
                            label="Term"
                            placeholder="e.g., OH"
                            {...createForm.getInputProps("term")}
                        />
                        <TextInput
                            label="Full Name"
                            placeholder="e.g., Operation Hours"
                            {...createForm.getInputProps("full_name")}
                        />
                        <Textarea
                            label="Description"
                            placeholder="Describe what this term means"
                            minRows={3}
                            {...createForm.getInputProps("description")}
                        />
                        <Button type="submit" loading={isCreating} fullWidth>
                            Create Term
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            <Modal
                closeButtonProps={{ "aria-label": "Close modal" }}
                opened={detailsModalOpened}
                onClose={() => {
                    setDetailsModalOpened(false);
                    setSelectedTerm(null);
                }}
                title={`Term: ${selectedTerm?.term ?? ""}`}
                size="md"
            >
                <Stack gap="md">
                    <TextInput label="Term" value={selectedTerm?.term ?? ""} readOnly />
                    <TextInput label="Full Name" value={selectedTerm?.full_name ?? ""} readOnly />
                    <Textarea
                        label="Description"
                        value={selectedTerm?.description ?? ""}
                        readOnly
                        autosize
                        minRows={3}
                    />
                    <Group justify="flex-end" mt="md">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDetailsModalOpened(false);
                                setSelectedTerm(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button color="red" onClick={() => setDeleteModalOpened(true)}>
                            Delete Term
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                opened={deleteModalOpened}
                onClose={() => {
                    setDeleteModalOpened(false);
                }}
                title={`Delete Term "${selectedTerm?.term ?? ""}"?`}
                size="md"
            >
                <Box>
                    <Text mb="md">
                        Are you sure you want to delete this term? This action cannot be undone.
                    </Text>
                    <Flex gap="md" justify="flex-end">
                        <Button variant="outline" onClick={() => setDeleteModalOpened(false)}>
                            Cancel
                        </Button>
                        <Button color="red" loading={isDeleting} onClick={handleDelete}>
                            Delete
                        </Button>
                    </Flex>
                </Box>
            </Modal>
        </>
    );
};

export default ManageTerms;
